import { useState } from "react";
import type { Player, Team } from "@spikeeasy/tournament";
import { api } from "./api";
import { Dialog, Field, Notice, teamLabel } from "./ui";

const points = (team: Team) => team.players[0].points + team.players[1].points;
const reseed = (teams: Team[]) =>
  teams.map((team, i) => ({ ...team, seed: i + 1 }));

export function TeamBuilder({
  teams,
  players,
  changed,
  playerCreated,
}: {
  teams: Team[];
  players: Player[];
  changed: (teams: Team[]) => void;
  playerCreated: (player: Player) => void;
}) {
  const [editing, setEditing] = useState<Team | "new" | null>(null);
  return (
    <div className="stack">
      <p>
        Choose two players per team. Add a new profile here if they are not in
        the player list yet.
      </p>
      <div className="section-heading">
        <h2>
          {teams.length} {teams.length === 1 ? "team" : "teams"} ·{" "}
          {teams.filter((t) => t.bringsNet).length}{" "}
          {teams.filter((t) => t.bringsNet).length === 1 ? "net" : "nets"}
        </h2>
        <button
          className="primary"
          disabled={teams.length >= 50}
          onClick={() => setEditing("new")}
        >
          Add team
        </button>
      </div>
      {teams.length ? (
        <>
          <button
            className="text-button"
            onClick={() =>
              changed(reseed(teams.toSorted((a, b) => points(b) - points(a))))
            }
          >
            Seed by player points
          </button>
          <ol className="team-builder-list">
            {teams.map((team, index) => (
              <li key={team.id}>
                <div className="stack team-summary">
                  <strong>{teamLabel(team)}</strong>
                  <span>{team.players.map((p) => p.name).join(" + ")}</span>
                  <small>
                    {points(team).toLocaleString()} pts
                    {team.bringsNet ? " · Brings a net" : ""}
                  </small>
                </div>
                <div className="team-row-actions">
                  <Field label="Seed">
                    <select
                      aria-label={`Seed for ${team.name}`}
                      value={index}
                      onChange={(e) => {
                        const next = [...teams];
                        next.splice(index, 1);
                        next.splice(Number(e.target.value), 0, team);
                        changed(reseed(next));
                      }}
                    >
                      {teams.map((_, i) => (
                        <option key={i} value={i}>
                          {i + 1}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <button
                    className="text-button"
                    aria-label={`Edit ${team.name}`}
                    onClick={() => setEditing(team)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-button danger-text"
                    aria-label={`Remove ${team.name}`}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove ${team.name} from this draft? Both player profiles will stay in the database.`,
                        )
                      )
                        changed(reseed(teams.filter((t) => t.id !== team.id)));
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <Notice>
          Add your first team. A tournament needs between 8 and 50 teams.
        </Notice>
      )}
      {editing && (
        <TeamEditor
          team={editing === "new" ? undefined : editing}
          teams={teams}
          players={players}
          playerCreated={playerCreated}
          close={() => setEditing(null)}
          saved={(team) => {
            changed(
              reseed(
                editing === "new"
                  ? [...teams, team]
                  : teams.map((t) => (t.id === team.id ? team : t)),
              ),
            );
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function TeamEditor({
  team,
  teams,
  players,
  playerCreated,
  close,
  saved,
}: {
  team?: Team;
  teams: Team[];
  players: Player[];
  playerCreated: (player: Player) => void;
  close: () => void;
  saved: (team: Team) => void;
}) {
  const [name, setName] = useState(team?.name ?? "");
  const [selected, setSelected] = useState<[string, string]>([
    team?.players[0].id ?? "",
    team?.players[1].id ?? "",
  ]);
  const [bringsNet, setBringsNet] = useState(team?.bringsNet ?? false);
  const [creating, setCreating] = useState<number | null>(null);
  const [error, setError] = useState("");
  const used = new Set(
    teams
      .filter((t) => t.id !== team?.id)
      .flatMap((t) => t.players.map((p) => p.id)),
  );
  const choose = (index: number, id: string) =>
    setSelected((current) =>
      index === 0 ? [id, current[1]] : [current[0], id],
    );
  const selectedPlayers = selected.map((id) =>
    players.find((p) => p.id === id),
  );
  return (
    <Dialog
      title={team ? "Edit team" : "Add team"}
      onClose={() => {
        if (creating === null) close();
      }}
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          const [a, b] = selectedPlayers;
          if (creating !== null) return;
          if (!a || !b || a.id === b.id || used.has(a.id) || used.has(b.id)) {
            setError(
              "Choose two different players who are not on another team.",
            );
            return;
          }
          saved({
            id: team?.id ?? crypto.randomUUID(),
            name: name.trim() || `${a.name} + ${b.name}`.slice(0, 120),
            players: [a, b],
            bringsNet,
            seed: team?.seed ?? teams.length + 1,
            withdrawn: false,
          });
        }}
      >
        <Field label="Team name (optional)">
          <input
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Uses the two players’ names if left blank"
          />
        </Field>
        <div className="team-player-fields">
          {[0, 1].map((index) => (
            <PlayerPicker
              key={index}
              label={`Player ${index + 1}`}
              selected={selected[index]!}
              players={players.filter(
                (p) => !used.has(p.id) && p.id !== selected[1 - index],
              )}
              disabled={creating !== null}
              creating={creating === index}
              choose={(id) => choose(index, id)}
              create={async (name) => {
                setCreating(index);
                setError("");
                try {
                  const { player } = await api<{ player: Player }>("/players", {
                    name,
                  });
                  playerCreated(player);
                  choose(index, player.id);
                  return true;
                } catch (e) {
                  setError((e as Error).message);
                  return false;
                } finally {
                  setCreating(null);
                }
              }}
            />
          ))}
        </div>
        <p>
          <strong>
            {selectedPlayers
              .reduce((sum, p) => sum + (p?.points ?? 0), 0)
              .toLocaleString()}{" "}
            team points
          </strong>
        </p>
        <label className="check">
          <input
            type="checkbox"
            checked={bringsNet}
            onChange={(e) => setBringsNet(e.target.checked)}
          />
          Brings a net
        </label>
        {error && <Notice error>{error}</Notice>}
        <div className="actions">
          <button
            className="primary"
            disabled={creating !== null || selectedPlayers.some((p) => !p)}
          >
            {team ? "Save team" : "Add team"}
          </button>
          <button
            type="button"
            className="text-button"
            disabled={creating !== null}
            onClick={close}
          >
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function PlayerPicker({
  label,
  selected,
  players,
  disabled,
  creating,
  choose,
  create,
}: {
  label: string;
  selected: string;
  players: Player[];
  disabled: boolean;
  creating: boolean;
  choose: (id: string) => void;
  create: (name: string) => Promise<boolean>;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const matches = players
    .filter(
      (p) =>
        p.id === selected ||
        p.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
    )
    .toSorted((a, b) => a.name.localeCompare(b.name));
  return (
    <fieldset className="player-picker stack" disabled={disabled}>
      <legend>{label}</legend>
      {adding ? (
        <>
          <Field
            label="New player’s full name"
            hint="Saved to the player database immediately, with zero points."
          >
            <input
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (name.trim() && !disabled && (await create(name.trim())))
                    setAdding(false);
                }
              }}
            />
          </Field>
          <div className="actions">
            <button
              type="button"
              className="secondary"
              disabled={disabled || !name.trim()}
              onClick={async () => {
                if (await create(name.trim())) setAdding(false);
              }}
            >
              {creating ? "Creating…" : "Create & select player"}
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => setAdding(false)}
            >
              Choose existing instead
            </button>
          </div>
        </>
      ) : (
        <>
          {players.length > 12 && (
            <Field label={`Search ${label.toLowerCase()}`}>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Field>
          )}
          <Field label={`Choose ${label.toLowerCase()}`}>
            <select value={selected} onChange={(e) => choose(e.target.value)}>
              <option value="">Select a player</option>
              {matches.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.points.toLocaleString()} pts
                  {players.some(
                    (other) => other.id !== p.id && other.name === p.name,
                  )
                    ? ` · ${p.id.slice(-8)}`
                    : ""}
                </option>
              ))}
            </select>
          </Field>
          {!matches.length && (
            <small>
              {query ? "No matching players." : "No available players yet."}
            </small>
          )}
          <button
            type="button"
            className="text-button"
            onClick={() => {
              choose("");
              setName(query);
              setAdding(true);
            }}
          >
            Create new player
          </button>
        </>
      )}
    </fieldset>
  );
}
